import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import UIEmptyState from 'components/ui/UIEmptyState';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIContainer from 'components/ui/UIContainer';
import WithLoader from 'components/WithLoader';
import WordsTable, { RowsSelected } from 'components/WordsTable';
import {
  InActionAnalysisGroup,
  InActionList,
  InActionWord,
  UserIgnoredWord,
} from 'app/types';
import { axiosInstance } from 'setup/axiosInstance';
import { useNavigate, useParams } from 'react-router-dom';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { NotificationCenterContext } from 'components/NotificationCenter';
import TableSettingsDialog from 'components/TableSettingsDialog';
import {
  createDefaultColumnSettings,
  DEFAULT_TABLE_PAGE_SIZE,
  tableSettingsToColumnsVisibility,
} from 'components/tableSettings';
import { useTableSettings } from 'hooks/useTableSettings';
import ListStatHeader from './components/ListStatHeader';
import SourceTextDialog from './components/SourceTextDialog';
import ContextualActionBar from './components/ContextualActionBar';
import EnrichCTA from './components/EnrichCTA';
import IgnoredWordsPanel from './components/IgnoredWordsPanel';
import NameIgnoreCTA from './components/NameIgnoreCTA';
import {
  computeListProgress,
  MOCK_SOURCE_TEXT,
  mockLastSession,
} from './mockInAction';

type VocabularyWordStatus = 'learned' | 'notLearned' | 'repeat';
type AddTarget = 'words' | 'verbs';

type InActionTab =
  | 'verbs'
  | 'learned'
  | 'needsRepeat'
  | 'notLearned'
  | 'new'
  | 'attention';

interface WordTabConfig {
  group: InActionAnalysisGroup;
  label: string;
  value: Exclude<InActionTab, 'verbs'>;
}

// Tab strip order, left→right. Most-actionable group leftmost so users land
// on what needs work; verbs (separate kind) sit at the far right.
const wordTabs: WordTabConfig[] = [
  { group: 'NOT_IN_VOCABULARY', label: 'Потребують уваги', value: 'attention' },
  { group: 'NOT_IN_VOCABULARY', label: 'Нові', value: 'new' },
  { group: 'NOT_LEARNED_IN_VOCABULARY', label: 'Не вивчені', value: 'notLearned' },
  { group: 'NEEDS_REPEAT', label: 'Потребують повторення', value: 'needsRepeat' },
  { group: 'LEARNED_ACTIVE', label: 'Вивчені', value: 'learned' },
];

const statusOptions: {
  label: string;
  value: VocabularyWordStatus;
  isActiveSpeech: boolean;
  isLearned: boolean;
}[] = [
  { label: 'Не вивчено', value: 'notLearned', isLearned: false, isActiveSpeech: false },
  { label: 'Вивчено', value: 'learned', isLearned: true, isActiveSpeech: false },
  { label: 'Потребує повторення', value: 'repeat', isLearned: true, isActiveSpeech: false },
];

const DEFAULT_IN_ACTION_WORD_COLUMNS = createDefaultColumnSettings({
  number: true,
  word: true,
  translation: true,
  partOfSpeech: true,
  definition: true,
  isLearned: true,
  isActiveSpeech: true,
});
const DEFAULT_IN_ACTION_VERB_COLUMNS = createDefaultColumnSettings({
  number: true,
  infinitive: true,
  infinitiveTranscription: true,
  pastSimple: true,
  pastSimpleTranscription: true,
  pastParticiple: true,
  pastParticipleTranscription: true,
  translation: true,
  isLearned: true,
  isActiveSpeech: true,
});

const normalizePartOfSpeech = (value?: string) => {
  if (!value || value === 'IRREGULAR_VERB') return null;
  return value.trim().toUpperCase().replaceAll(' ', '_');
};

const getWordLabel = (word: InActionWord) =>
  word.wordKind === 'IRREGULAR_VERB' ? word.infinitive || word.word : word.word;

const hasMissingTranslation = (word: InActionWord) => {
  const translation = word.translation?.trim().toLowerCase();
  return !translation || translation === 'відсутній';
};

// Visual stand-in for lemma-known words that have no translation yet — they
// live on the "Нові" tab waiting for the LLM batch enrichment to fill them in.
// Replaces the empty translation cell so the row isn't visually broken.
const PENDING_TRANSLATION_PLACEHOLDER = '— готується…';
const overlayPendingTranslation = (word: InActionWord): string => {
  if (word.inLemmaTable && hasMissingTranslation(word)) {
    return PENDING_TRANSLATION_PLACEHOLDER;
  }
  return word.translation || '';
};

const sortInActionWords = <T extends InActionWord>(items: T[]) =>
  [...items].sort((a, b) =>
    getWordLabel(a).localeCompare(getWordLabel(b), undefined, {
      sensitivity: 'base',
    }),
  );

const InActionListPage: React.FC = () => {
  const { inActionListId = '' } = useParams();
  const navigate = useNavigate();
  const { pushNotification } = useContext(NotificationCenterContext);
  const [list, setList] = useState<InActionList | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  // Default tab is "Не вивчені" — most actionable group for the typical
  // learner. "Потребують уваги" is intentionally last (rightmost) since it's
  // usually small but has its own ignore-list management UI.
  const [activeTab, setActiveTab] = useState<InActionTab>('notLearned');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pendingRowsSelected, setPendingRowsSelected] = useState<RowsSelected>({});
  const [pendingTarget, setPendingTarget] = useState<AddTarget>('words');
  const [selectedStatus, setSelectedStatus] =
    useState<VocabularyWordStatus>('notLearned');
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [currentSelection, setCurrentSelection] = useState<RowsSelected>({});
  const [visibleCount, setVisibleCount] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [settingsAnchor, setSettingsAnchor] = useState<{
    target: AddTarget;
    anchor: HTMLElement;
  } | null>(null);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  // Account-wide ignore list, loaded from BE on mount. Mutations push to
  // PUT /api/in-action/ignored-words (replace semantics) so the list stays
  // in sync across tabs/sessions.
  //
  // Casing: stored verbatim so the chips display the original form ("Arya",
  // not "arya"). Matching against text is case-insensitive — see the derived
  // `ignoredLower` set below.
  const [ignoredWords, setIgnoredWords] = useState<Set<string>>(
    () => new Set(),
  );
  const ignoredLower = useMemo(
    () => new Set([...ignoredWords].map((w) => w.toLowerCase())),
    [ignoredWords],
  );

  /**
   * Optimistic: applies the next set locally, then PUTs it to the BE.
   * On failure rolls back to `prevWords` and surfaces a toast — the user
   * sees the chip pop back, not a silent ignored mutation.
   */
  const updateIgnoredWords = async (
    next: Set<string>,
    prevWords: Set<string>,
  ) => {
    setIgnoredWords(next);
    try {
      await axiosInstance.put('/in-action/ignored-words', {
        words: [...next],
      });
    } catch {
      setIgnoredWords(prevWords);
      pushNotification(
        'Не вдалось зберегти ігнор-список',
        'error',
        undefined,
        'center',
      );
    }
  };
  // Local override: word IDs the user has manually promoted from "Потребують
  // уваги" to "Нові". Treats them as inLemmaTable=true for filter/totals.
  // Real BE: POST /api/in-action/lists/:id/words/:wid/promote-to-lemma which
  // flips InActionWord.inLemmaTable and (optionally) inserts the form into the
  // global word_lemma with source="user_review".
  const [promotedToNew, setPromotedToNew] = useState<Set<number>>(new Set());
  const [exerciseDialogOpen, setExerciseDialogOpen] = useState(false);
  const [exerciseTarget, setExerciseTarget] = useState<AddTarget>('words');
  const [exerciseSelectedRows, setExerciseSelectedRows] = useState<RowsSelected>({});
  const [startingExercise, setStartingExercise] = useState(false);
  const {
    resetSettings: resetWordTableSettings,
    saveSettings: saveWordTableSettings,
    settings: wordTableSettings,
  } = useTableSettings('IN_ACTION_WORDS', DEFAULT_IN_ACTION_WORD_COLUMNS);
  const {
    resetSettings: resetVerbTableSettings,
    saveSettings: saveVerbTableSettings,
    settings: verbTableSettings,
  } = useTableSettings(
    'IN_ACTION_IRREGULAR_VERBS',
    DEFAULT_IN_ACTION_VERB_COLUMNS,
  );
  const wordTableColumns = useMemo(
    () => tableSettingsToColumnsVisibility(wordTableSettings, { select: true }),
    [wordTableSettings],
  );
  const verbTableColumns = useMemo(
    () => tableSettingsToColumnsVisibility(verbTableSettings, { select: true }),
    [verbTableSettings],
  );

  const rawAllWords = useMemo(() => list?.words || [], [list?.words]);

  // Visible list = source list minus user-ignored words. Counted in stats too.
  const isIgnored = useCallback(
    (w: InActionWord) => !!w.word && ignoredLower.has(w.word.toLowerCase()),
    [ignoredLower],
  );
  const allWords = useMemo(
    () =>
      rawAllWords
        .filter((w) => !isIgnored(w))
        // Manual promotion overrides the snapshot flag so the word sorts into
        // "Нові" right away. The override is local until the BE persists it.
        .map((w) =>
          w.id && promotedToNew.has(w.id) ? { ...w, inLemmaTable: true } : w,
        ),
    [rawAllWords, isIgnored, promotedToNew],
  );
  // Ignored words "in this list". Two sources are merged so the panel works
  // both for snapshot-aware lists (new) and snapshot-less lists (old or where
  // the user ignored words AFTER analyze):
  //   A. Persisted snapshot ∩ live global ignore — for words BE filtered out
  //      at analyze time (they were never written into list.words).
  //   B. Words present in list.words whose display form is currently in the
  //      global ignore set — covers old lists pre-snapshot, and any word the
  //      user ignored from this list view after the analyze.
  // Restore from the panel pulls the word out of the global set → the chip
  // disappears naturally on the next render.
  const listScopedIgnored = useMemo(() => {
    const collected = new Map<string, string>();

    list?.ignoredAtAnalyze?.forEach((entry) => {
      if (!entry?.wordNormalized) return;
      if (ignoredLower.has(entry.wordNormalized)) {
        collected.set(entry.wordNormalized, entry.word || entry.wordNormalized);
      }
    });

    rawAllWords.forEach((w) => {
      const display = (w.word || '').trim();
      if (!display) return;
      const lower = display.toLowerCase();
      if (ignoredLower.has(lower) && !collected.has(lower)) {
        collected.set(lower, display);
      }
    });

    return [...collected.values()].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [list?.ignoredAtAnalyze, ignoredLower, rawAllWords]);
  const ignoredCount = listScopedIgnored.length;

  const words = useMemo(
    () => sortInActionWords(allWords.filter((w) => w.wordKind === 'WORD')),
    [allWords],
  );
  const verbs = useMemo(
    () =>
      sortInActionWords(allWords.filter((w) => w.wordKind === 'IRREGULAR_VERB')),
    [allWords],
  );

  const wordsByTab = useMemo(
    () =>
      wordTabs.reduce<Record<Exclude<InActionTab, 'verbs'>, InActionWord[]>>(
        (acc, tab) => ({
          ...acc,
          [tab.value]: words.filter((w) => {
            if (w.analysisGroup !== tab.group) return false;
            // Both "new" and "attention" map to NOT_IN_VOCABULARY. Split by
            // translation presence only — BNC-lemma-driven routing produced
            // too many false negatives, so a missing translation is the sole
            // signal for "attention".
            if (tab.value === 'attention') {
              return hasMissingTranslation(w);
            }
            if (tab.value === 'new') {
              return !hasMissingTranslation(w);
            }
            return true;
          }),
        }),
        {
          learned: [],
          needsRepeat: [],
          notLearned: [],
          new: [],
          attention: [],
        },
      ),
    [words],
  );

  // Counts for stat header — derived once from the full list.
  const totals = useMemo(() => {
    const total = words.length + verbs.length;
    const learned = allWords.filter(
      (w) => w.analysisGroup === 'LEARNED_ACTIVE',
    ).length;
    const notLearned = allWords.filter(
      (w) => w.analysisGroup === 'NOT_LEARNED_IN_VOCABULARY',
    ).length;
    const outOfVocab = allWords.filter(
      (w) =>
        w.analysisGroup === 'NOT_IN_VOCABULARY' && !hasMissingTranslation(w),
    ).length;
    const needsAttention = allWords.filter(
      (w) =>
        w.analysisGroup === 'NOT_IN_VOCABULARY' && hasMissingTranslation(w),
    ).length;
    return {
      total,
      learned,
      notLearned,
      outOfVocab,
      needsAttention,
      ignored: ignoredCount,
    };
  }, [allWords, words.length, verbs.length, ignoredCount]);

  const progress = useMemo(() => computeListProgress(allWords), [allWords]);
  const lastSession = useMemo(
    () => (inActionListId ? mockLastSession(inActionListId) : undefined),
    [inActionListId],
  );

  const activeRecords =
    activeTab === 'verbs' ? verbs : wordsByTab[activeTab];

  const activePageSize =
    activeTab === 'verbs'
      ? verbTableSettings.pageSize
      : wordTableSettings.pageSize;
  const totalPages = Math.max(1, Math.ceil(activeRecords.length / activePageSize));
  const currentPage = Math.ceil(visibleCount / activePageSize);
  const paginatedActiveRecords = activeRecords.slice(0, visibleCount);

  // Drilling makes no sense for words with no translation — gate the
  // contextual "Сесія" button on the attention tab.
  const exerciseDisabledOnAttention = activeTab === 'attention';

  // Proper-name candidates on the attention tab. Heuristic: first letter is
  // uppercase. The BE-side lowercase-normalisation will collapse this to zero
  // until that part of the pipeline preserves casing — the empty state is
  // honest then.
  const nameCandidates = useMemo(() => {
    if (activeTab !== 'attention') return [] as string[];
    const seen = new Set<string>();
    wordsByTab.attention.forEach((w) => {
      const raw = (w.word || '').trim();
      if (raw && /^[A-Z]/.test(raw)) {
        seen.add(raw);
      }
    });
    return [...seen];
  }, [activeTab, wordsByTab.attention]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<InActionList>(
        `/in-action/lists/${inActionListId}`,
      );
      setList(data);
    } finally {
      setLoading(false);
    }
  }, [inActionListId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Load the global ignore list once on page open. Failure is non-fatal —
  // the page still renders, just with no chips and no filter applied.
  useEffect(() => {
    const loadIgnoredWords = async () => {
      try {
        const { data } = await axiosInstance.get<UserIgnoredWord[]>(
          '/in-action/ignored-words',
        );
        setIgnoredWords(new Set(data.map((entry) => entry.word)));
      } catch {
        // swallow — keep the local set empty
      }
    };
    loadIgnoredWords();
  }, []);

  useEffect(() => {
    setVisibleCount(activePageSize);
    setPendingRowsSelected({});
    setCurrentSelection({});
    setSelectionResetKey((p) => p + 1);
  }, [activePageSize, activeTab, list?.id]);

  const openAddDialog = (target: AddTarget, rowsSelected: RowsSelected) => {
    setPendingTarget(target);
    setPendingRowsSelected(rowsSelected);
    setSelectedStatus('notLearned');
    setAddDialogOpen(true);
  };
  const closeAddDialog = () => setAddDialogOpen(false);

  const addSelectedToVocabulary = async () => {
    const selectedOption = statusOptions.find(
      ({ value }) => value === selectedStatus,
    );
    const selectedCount =
      Object.values(pendingRowsSelected).filter(Boolean).length;
    if (!selectedOption || !selectedCount || !list) return;

    setAdding(true);
    try {
      if (pendingTarget === 'words') {
        await axiosInstance.post(
          `/vocabulary/words`,
          words
            .filter(({ id }) => id !== undefined && pendingRowsSelected[id])
            .map((word) => ({
              id: word.dictionaryId,
              wordType: 'WORD',
              word: word.word,
              transcription: '',
              translation: word.translation,
              isLearned: selectedOption.isLearned,
              isActiveSpeech: selectedOption.isActiveSpeech,
              partOfSpeech: normalizePartOfSpeech(word.partOfSpeech),
              definition: word.definition,
            })),
        );
      } else {
        await axiosInstance.post(
          `/vocabulary/verbs/`,
          verbs
            .filter(({ id }) => id !== undefined && pendingRowsSelected[id])
            .filter((verb) => !!verb.dictionaryIrregularVerbId)
            .map((verb) => ({
              id: verb.dictionaryIrregularVerbId,
              levelBased: verb.levelBased,
              infinitive: verb.infinitive || verb.word,
              infinitiveTranscription: verb.infinitiveTranscription,
              pastSimple: verb.pastSimple,
              pastSimpleTranscription: verb.pastSimpleTranscription,
              pastParticiple: verb.pastParticiple,
              pastParticipleTranscription: verb.pastParticipleTranscription,
              translation: verb.translation,
              description: verb.description || verb.definition,
              isLearned: selectedOption.isLearned,
              isActiveSpeech: selectedOption.isActiveSpeech,
            })),
        );
      }

      setAddDialogOpen(false);
      setPendingRowsSelected({});
      setCurrentSelection({});
      setSelectionResetKey((p) => p + 1);
      pushNotification(
        `Додано ${selectedCount} слів в словник`,
        'success',
        undefined,
        'center',
      );
      await loadList();
    } finally {
      setAdding(false);
    }
  };

  const buildExerciseSelection = (
    target: AddTarget,
    rows: RowsSelected,
  ): InActionWord[] => {
    if (target === 'verbs') {
      return verbs
        .filter(({ id }) => id !== undefined && rows[id])
        .filter((v) => !!v.dictionaryIrregularVerbId);
    }
    return words.filter(({ id }) => id !== undefined && rows[id]);
  };

  const hasItemWithoutVocab = (items: InActionWord[], target: AddTarget): boolean =>
    target === 'verbs'
      ? items.some((v) => !v.vocabularyIrregularVerbId)
      : items.some((w) => !w.vocabularyId);

  const allItemsLackVocab = (items: InActionWord[], target: AddTarget): boolean =>
    target === 'verbs'
      ? items.every((v) => !v.vocabularyIrregularVerbId)
      : items.every((w) => !w.vocabularyId);

  const openStartExerciseDialog = (target: AddTarget, rows: RowsSelected) => {
    const selected = buildExerciseSelection(target, rows);
    if (!selected.length) return;
    setExerciseTarget(target);
    setExerciseSelectedRows(rows);
    if (hasItemWithoutVocab(selected, target)) {
      setExerciseDialogOpen(true);
    } else {
      void startExercise(false);
    }
  };

  const closeExerciseDialog = () => {
    if (startingExercise) return;
    setExerciseDialogOpen(false);
  };

  const startExercise = async (saveNewToVocab: boolean) => {
    const selected = buildExerciseSelection(exerciseTarget, exerciseSelectedRows);
    if (!selected.length || !list) return;
    setStartingExercise(true);
    try {
      const sessionId =
        exerciseTarget === 'verbs'
          ? await startVerbsSession(selected, saveNewToVocab)
          : await startWordsSession(selected, saveNewToVocab);
      setExerciseDialogOpen(false);
      setPendingRowsSelected({});
      setSelectionResetKey((p) => p + 1);
      navigate(
        fillUrlIds(pagePaths.learningSession, {
          learningSessionId: String(sessionId),
        }),
      );
    } catch {
      pushNotification(
        'Не вдалось створити сесію. Спробуй ще раз.',
        'error',
        undefined,
        'center',
      );
    } finally {
      setStartingExercise(false);
    }
  };

  const startWordsSession = async (
    selected: InActionWord[],
    saveNewToVocab: boolean,
  ): Promise<number> => {
    const inVocab = selected.filter((w) => !!w.vocabularyId);
    const newOnes = selected.filter((w) => !w.vocabularyId);

    if (newOnes.length === 0) {
      const resp = await axiosInstance.post<{ id: number }>(
        '/learning-sessions',
        {
          mode: 'MANUAL',
          source: 'HEADER',
          wordIds: inVocab.map((w) => w.vocabularyId),
          sourceInActionListId: Number(inActionListId),
        },
      );
      return resp.data.id;
    }

    if (!saveNewToVocab && inVocab.length === 0) {
      const dictionaryIds = newOnes
        .map((w) => w.dictionaryId)
        .filter((id): id is number => !!id);
      const resp = await axiosInstance.post<{ id: number }>(
        '/learning-sessions',
        {
          mode: 'MANUAL',
          source: 'DICTIONARY',
          wordIds: dictionaryIds,
          sourceInActionListId: Number(inActionListId),
        },
      );
      return resp.data.id;
    }

    const created = await axiosInstance.post<{ id: number }[]>(
      `/vocabulary/words`,
      newOnes.map((word) => ({
        id: word.dictionaryId,
        wordType: 'WORD',
        word: word.word,
        transcription: '',
        translation: word.translation,
        isLearned: false,
        isActiveSpeech: false,
        partOfSpeech: normalizePartOfSpeech(word.partOfSpeech),
        definition: word.definition,
      })),
    );
    const allVocabIds = [
      ...inVocab.map((w) => w.vocabularyId as number),
      ...created.data.map((v) => v.id),
    ];
    const resp = await axiosInstance.post<{ id: number }>(
      '/learning-sessions',
      {
        mode: 'MANUAL',
        source: 'HEADER',
        wordIds: allVocabIds,
        sourceInActionListId: Number(inActionListId),
      },
    );
    return resp.data.id;
  };

  const startVerbsSession = async (
    selected: InActionWord[],
    saveNewToVocab: boolean,
  ): Promise<number> => {
    const inVocab = selected.filter((v) => !!v.vocabularyIrregularVerbId);
    const newOnes = selected.filter((v) => !v.vocabularyIrregularVerbId);

    if (newOnes.length === 0) {
      const resp = await axiosInstance.post<{ id: number }>(
        '/learning-sessions',
        {
          mode: 'MANUAL',
          source: 'HEADER',
          irregularVerbIds: inVocab.map((v) => v.vocabularyIrregularVerbId),
          sourceInActionListId: Number(inActionListId),
        },
      );
      return resp.data.id;
    }

    if (!saveNewToVocab && inVocab.length === 0) {
      const dictionaryIds = newOnes
        .map((v) => v.dictionaryIrregularVerbId)
        .filter((id): id is number => !!id);
      const resp = await axiosInstance.post<{ id: number }>(
        '/learning-sessions',
        {
          mode: 'MANUAL',
          source: 'DICTIONARY',
          irregularVerbIds: dictionaryIds,
          sourceInActionListId: Number(inActionListId),
        },
      );
      return resp.data.id;
    }

    const created = await axiosInstance.post<{ id: number }[]>(
      `/vocabulary/verbs/`,
      newOnes.map((verb) => ({
        id: verb.dictionaryIrregularVerbId,
        levelBased: verb.levelBased,
        infinitive: verb.infinitive || verb.word,
        infinitiveTranscription: verb.infinitiveTranscription,
        pastSimple: verb.pastSimple,
        pastSimpleTranscription: verb.pastSimpleTranscription,
        pastParticiple: verb.pastParticiple,
        pastParticipleTranscription: verb.pastParticipleTranscription,
        translation: verb.translation,
        description: verb.description || verb.definition,
        isLearned: false,
        isActiveSpeech: false,
      })),
    );
    const allVocabIds = [
      ...inVocab.map((v) => v.vocabularyIrregularVerbId as number),
      ...created.data.map((v) => v.id),
    ];
    const resp = await axiosInstance.post<{ id: number }>(
      '/learning-sessions',
      {
        mode: 'MANUAL',
        source: 'HEADER',
        irregularVerbIds: allVocabIds,
        sourceInActionListId: Number(inActionListId),
      },
    );
    return resp.data.id;
  };

  const changePage = (page: number) => {
    setVisibleCount(Math.min(page * activePageSize, activeRecords.length));
  };

  // WordsTable always expects a renderToolbar. We hook into it to:
  // (1) keep the local copy of the row-selection state so the floating bar
  //     outside the table can show it, and
  // (2) host the table-settings cog discreetly above the table on the right.
  const renderToolbar =
    (target: AddTarget) =>
    // Render prop: WordsTable calls this as a function, not as a JSX element,
    // so there is no component instance that needs a display name.
    // eslint-disable-next-line react/display-name
    ({ rowsSelected }: { rowsSelected: RowsSelected }) => {
      // Sync selection up — the contextual bar lives outside this render.
      if (rowsSelected !== currentSelection) {
        Promise.resolve().then(() => setCurrentSelection(rowsSelected));
      }
      return (
        <Stack direction={'row'} justifyContent={'flex-end'} sx={{ mt: 1.5 }}>
          <IconButton
            aria-label={'Налаштування таблиці'}
            onClick={(e) =>
              setSettingsAnchor({ target, anchor: e.currentTarget })
            }
            size={'small'}
          >
            <SettingsIcon fontSize={'small'} />
          </IconButton>
        </Stack>
      );
    };

  const currentTarget: AddTarget = activeTab === 'verbs' ? 'verbs' : 'words';
  const selectedCountOutside =
    Object.values(currentSelection).filter(Boolean).length;
  const clearSelection = () => {
    setCurrentSelection({});
    setSelectionResetKey((p) => p + 1);
  };

  const ignoreSelected = () => {
    const pool = currentTarget === 'verbs' ? verbs : words;
    const toIgnore = pool
      .filter(({ id }) => id !== undefined && currentSelection[id])
      .map((w) => (w.word || '').trim())
      .filter(Boolean);
    if (toIgnore.length === 0) return;
    const prevSet = ignoredWords;
    const nextSet = new Set(prevSet);
    toIgnore.forEach((w) => nextSet.add(w));
    void updateIgnoredWords(nextSet, prevSet);
    pushNotification(
      `Додано ${toIgnore.length} слів у ігнор-список`,
      'success',
      undefined,
      'center',
    );
    clearSelection();
  };

  const restoreIgnored = (wordDisplayForm: string) => {
    // The snapshot's display form may not byte-equal what's in the global
    // set (e.g. global has "Cersei", snapshot stored "CERSEI"). Match by
    // lowercase to be safe.
    const lower = wordDisplayForm.toLowerCase();
    const prevSet = ignoredWords;
    const nextSet = new Set(
      [...prevSet].filter((w) => w.toLowerCase() !== lower),
    );
    if (nextSet.size === prevSet.size) return;
    void updateIgnoredWords(nextSet, prevSet);
  };

  const promoteSelectedToNew = () => {
    const pool = currentTarget === 'verbs' ? verbs : words;
    const toPromote = pool
      .filter(({ id }) => id !== undefined && currentSelection[id])
      .map((w) => w.id as number)
      .filter((id) => !!id);
    if (toPromote.length === 0) return;
    setPromotedToNew((prev) => {
      const next = new Set(prev);
      toPromote.forEach((id) => next.add(id));
      return next;
    });
    pushNotification(
      `Переведено у "Нові": ${toPromote.length}`,
      'success',
      undefined,
      'center',
    );
    clearSelection();
  };

  const ignoreNameCandidates = () => {
    if (nameCandidates.length === 0) return;
    const prevSet = ignoredWords;
    const nextSet = new Set(prevSet);
    nameCandidates.forEach((w) => nextSet.add(w));
    void updateIgnoredWords(nextSet, prevSet);
    pushNotification(
      `У ігнор-список додано ${nameCandidates.length} імен`,
      'success',
      undefined,
      'center',
    );
  };

  const activeWordsForTable = (activeTab === 'verbs'
    ? []
    : paginatedActiveRecords
  ).map((word) => ({
    ...word,
    id: word.id || 0,
    wordType: 'WORD',
    translation: overlayPendingTranslation(word),
    isLearned: word.isLearned,
    isActiveSpeech: word.isActiveSpeech,
  }));

  const verbsForTable = paginatedActiveRecords.map((verb) => ({
    ...verb,
    id: verb.id || 0,
    infinitive: verb.infinitive || verb.word,
    description: verb.description || verb.definition,
    isLearned: verb.isLearned,
    isActiveSpeech: verb.isActiveSpeech,
  }));

  const tabCounts: Record<Exclude<InActionTab, 'verbs'>, number> = useMemo(
    () => ({
      learned: wordsByTab.learned.length,
      needsRepeat: wordsByTab.needsRepeat.length,
      notLearned: wordsByTab.notLearned.length,
      new: wordsByTab.new.length,
      attention: wordsByTab.attention.length,
    }),
    [wordsByTab],
  );

  // Hide tabs with 0 words so users don't see empty groups. The verbs tab
  // follows the same rule. If the currently active tab disappears (e.g. user
  // ignored the last word in it), fall back to the first visible one.
  const visibleWordTabs = wordTabs.filter((tab) => tabCounts[tab.value] > 0);
  const showVerbsTab = verbs.length > 0;

  useEffect(() => {
    const isVisible =
      activeTab === 'verbs'
        ? showVerbsTab
        : tabCounts[activeTab] > 0;

    if (isVisible) return;

    if (visibleWordTabs.length) {
      setActiveTab(visibleWordTabs[0].value);
    } else if (showVerbsTab) {
      setActiveTab('verbs');
    }
  }, [activeTab, showVerbsTab, tabCounts, visibleWordTabs]);

  return (
    <>
      <UIContainer>
        <BreadCrumbs currentLabel={list?.title || 'Список слів'} />
      </UIContainer>
      <UIContainer sx={{ mb: 8 }}>
        <WithLoader loading={loading}>
          <Stack spacing={3} py={3}>
            <ListStatHeader
              title={list?.title || 'Список слів'}
              totals={totals}
              progress={progress}
              lastSession={lastSession}
              onOpenSource={() => setSourceDialogOpen(true)}
            />

            {(visibleWordTabs.length > 0 || showVerbsTab) && (
              <Tabs
                value={activeTab}
                onChange={(_, v: InActionTab) => setActiveTab(v)}
                variant={'scrollable'}
                scrollButtons={'auto'}
              >
                {visibleWordTabs.map((tab) => (
                  <Tab
                    key={tab.value}
                    label={`${tab.label} (${tabCounts[tab.value]})`}
                    value={tab.value}
                  />
                ))}
                {showVerbsTab && (
                  <Tab
                    label={`Неправильні дієслова (${verbs.length})`}
                    value={'verbs'}
                  />
                )}
              </Tabs>
            )}

            {activeTab === 'attention' && (
              <Stack spacing={2}>
                {wordsByTab.attention.length > 0 && (
                  <EnrichCTA count={wordsByTab.attention.length} />
                )}
                <NameIgnoreCTA
                  count={nameCandidates.length}
                  onIgnore={ignoreNameCandidates}
                />
              </Stack>
            )}

            {/* Ignore panel is independent of the active tab — users need to
                review/remove their ignored words from any view. Hidden when
                there's nothing to show to avoid a noisy empty block. */}
            {listScopedIgnored.length > 0 && (
              <IgnoredWordsPanel
                userIgnored={listScopedIgnored}
                onRestore={restoreIgnored}
              />
            )}

            {!activeRecords.length && (
              <UIEmptyState
                icon={<LightbulbOutlinedIcon />}
                title={'У цій категорії немає слів'}
                description={
                  'Спробуй іншу вкладку — слова з цього списку можуть бути в інших категоріях.'
                }
              />
            )}

            {!!activeRecords.length && activeTab !== 'verbs' && (
              <WordsTable
                renderToolbar={renderToolbar('words')}
                words={activeWordsForTable}
                columns={wordTableColumns}
                selectionResetKey={selectionResetKey}
                tableSettings={wordTableSettings}
              />
            )}

            {!!activeRecords.length && activeTab === 'verbs' && (
              <WordsTable
                renderToolbar={renderToolbar('verbs')}
                words={verbsForTable}
                columns={verbTableColumns}
                selectionResetKey={selectionResetKey}
                showDescriptionInInfinitive
                tableSettings={verbTableSettings}
              />
            )}

            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  justifyContent: 'center',
                  mt: 2,
                }}
              >
                {Array.from({ length: totalPages }, (_, i) => {
                  const page = i + 1;
                  const isActive = page === currentPage;
                  return (
                    <Button
                      key={page}
                      onClick={() => changePage(page)}
                      variant={'outlined'}
                      sx={{
                        // Active page: solid primary fill + white text.
                        backgroundColor: isActive ? 'primary.main' : 'transparent',
                        borderColor: isActive ? 'primary.main' : 'divider',
                        borderWidth: 1,
                        color: isActive ? 'common.white' : 'text.primary',
                        fontSize: 16,
                        fontWeight: isActive ? 700 : 500,
                        height: 38,
                        minHeight: 38,
                        minWidth: 38,
                        p: 0,
                        '&:hover': {
                          backgroundColor: isActive
                            ? 'primary.main'
                            : 'transparent',
                          borderColor: 'primary.main',
                          color: isActive ? 'common.white' : 'text.primary',
                        },
                      }}
                    >
                      {page}
                    </Button>
                  );
                })}
              </Box>
            )}
          </Stack>
        </WithLoader>

        <ContextualActionBar
          count={selectedCountOutside}
          exerciseDisabled={
            currentTarget === 'words' && exerciseDisabledOnAttention
          }
          exerciseDisabledReason={
            'Спершу збагати ці слова перекладом — потім зможеш вчити їх у сесії.'
          }
          onAddToVocabulary={() =>
            openAddDialog(currentTarget, currentSelection)
          }
          onStartExercise={() =>
            openStartExerciseDialog(currentTarget, currentSelection)
          }
          onPromoteToNew={
            activeTab === 'attention' ? promoteSelectedToNew : undefined
          }
          onIgnore={ignoreSelected}
          onClear={clearSelection}
        />
      </UIContainer>

      <SourceTextDialog
        open={sourceDialogOpen}
        onClose={() => setSourceDialogOpen(false)}
        text={MOCK_SOURCE_TEXT}
        words={allWords}
      />

      <Dialog
        disableScrollLock
        open={addDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth={'xs'}
      >
        <DialogTitle
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          {`Виберіть стан для ${
            Object.values(pendingRowsSelected).filter(Boolean).length
          } обраних слів:`}
          <IconButton onClick={closeAddDialog} size={'small'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {statusOptions.map(({ label, value }) => (
            <Box
              key={value}
              onClick={() => setSelectedStatus(value)}
              sx={{
                alignItems: 'center',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                py: 1,
              }}
            >
              {label}
              <Checkbox
                checked={selectedStatus === value}
                disableTouchRipple
                onChange={() => setSelectedStatus(value)}
              />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>{'Скасувати'}</Button>
          <Button
            loading={adding}
            onClick={addSelectedToVocabulary}
            variant={'contained'}
          >
            {'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        disableScrollLock
        open={exerciseDialogOpen}
        onClose={closeExerciseDialog}
        fullWidth
        maxWidth={'xs'}
      >
        <DialogTitle
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          {'Запустити сесію навчання'}
          <IconButton onClick={closeExerciseDialog} size={'small'}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant={'body2'} color={'text.secondary'} mb={1}>
            {(() => {
              const selected = buildExerciseSelection(
                exerciseTarget,
                exerciseSelectedRows,
              );
              const newCount = selected.filter((item) =>
                exerciseTarget === 'verbs'
                  ? !item.vocabularyIrregularVerbId
                  : !item.vocabularyId,
              ).length;
              return `Серед обраних слів ${newCount} ще немає у твоєму словнику. Що з ними робимо?`;
            })()}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          {(() => {
            const selected = buildExerciseSelection(
              exerciseTarget,
              exerciseSelectedRows,
            );
            const allLackVocab = allItemsLackVocab(selected, exerciseTarget);
            return (
              <>
                <Button
                  disabled={startingExercise || !allLackVocab}
                  onClick={() => startExercise(false)}
                  variant={'outlined'}
                >
                  {'Просто відпрацювати'}
                </Button>
                <Button
                  loading={startingExercise}
                  onClick={() => startExercise(true)}
                  variant={'contained'}
                >
                  {'Зберегти у словник і вивчати'}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>

      <TableSettingsDialog
        anchorEl={
          settingsAnchor?.target === 'words' ? settingsAnchor.anchor : null
        }
        settings={wordTableSettings}
        title={'Налаштування таблиці In Action words'}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetWordTableSettings}
        onSave={saveWordTableSettings}
      />
      <TableSettingsDialog
        anchorEl={
          settingsAnchor?.target === 'verbs' ? settingsAnchor.anchor : null
        }
        settings={verbTableSettings}
        title={'Налаштування таблиці In Action irregular verbs'}
        onClose={() => setSettingsAnchor(null)}
        onReset={resetVerbTableSettings}
        onSave={saveVerbTableSettings}
      />
    </>
  );
};

export default InActionListPage;
